import * as React from 'react';

import { InAppNotice } from './in-app-notice';
import { useInstallOffer } from './install-offer';
import { IOS_PUSH_NEEDS_INSTALL } from './supported-platforms';

/**
 * The in-app install offer (`PLT-008` AC-5, AC-6, AC-7).
 *
 * ## The moment
 *
 * Raised from a confirmed registration and nowhere else. `install-offer.ts` holds the rules —
 * never before a completed registration, at most twice, never once installed — and the caller
 * supplies the one fact that belongs to a screen rather than to the browser. `PushNotice` is
 * shaped the same way for the same reason: the moment is a property of the surface, so a second
 * caller has to state its own reason for existing rather than inherit one.
 *
 * ## Two platforms, two shapes, one criterion
 *
 * On Chromium the offer is a button that raises the real dialogue from the member's own tap. On
 * iOS there is no dialogue to raise — Safari fires no `beforeinstallprompt` — so the offer is the
 * instruction, naming the menu and the item, because a member who cannot find Share gives up.
 * That split is `MEM-043`'s and is reused rather than restated: `installMechanism` decides, and
 * `install-app-card.tsx` draws the same two shapes on My Account.
 *
 * **The iOS copy carries one extra sentence, and it is AC-6.** On iOS, web push only reaches an
 * app that is on the home screen. A member who turns notifications on in an iOS browser tab and
 * then hears nothing concludes the product is broken; the honest thing is to say so at the point
 * the member could do something about it, which is here. The sentence is
 * {@link IOS_PUSH_NEEDS_INSTALL}, stated once in `supported-platforms.ts` and shown in both
 * places it applies.
 *
 * ## Declining costs nothing (AC-7)
 *
 * There is no feature behind this. Dismiss takes the notice down and the app is in exactly the
 * state it was in — the same shape `MEM-040` AC-5 gives the push offer. The only thing a member
 * who declines loses is push on iOS, which is a platform limitation rather than a punishment,
 * and which is why it is stated in the offer they are declining rather than discovered later.
 */
export function InstallOfferNotice({
  hasCompletedRegistration,
}: {
  /** The member is looking at a registration of their own that went through. */
  readonly hasCompletedRegistration: boolean;
}): React.ReactElement | null {
  const offer = useInstallOffer({ hasCompletedRegistration });
  const [busy, setBusy] = React.useState(false);

  if (!offer.shouldOffer) return null;

  const manual = offer.mechanism === 'manual-ios';

  return (
    <InAppNotice
      title={INSTALL_OFFER_TITLE}
      body={manual ? INSTALL_OFFER_IOS_BODY : INSTALL_OFFER_BODY}
      // Spread rather than `action={undefined}`: `exactOptionalPropertyTypes` is on, so an
      // explicit `undefined` is not assignable to an optional property. On iOS there is no
      // action to offer — Safari installs from its own Share menu — so the prop is absent
      // rather than present-and-empty.
      {...(manual
        ? {}
        : {
            action: {
              label: INSTALL_OFFER_ACTION,
              busy,
              onSelect: () => {
                setBusy(true);
                void offer.install().finally(() => {
                  setBusy(false);
                });
              },
            },
          })}
      onDismiss={offer.dismiss}
      dismissLabel={INSTALL_OFFER_DISMISS_LABEL}
      className="mt-3"
    />
  );
}

/**
 * The copy.
 *
 * It names the two things installing actually buys — full screen from the home screen, and an app
 * that opens when the venue wifi does not — because "install the app" on a website is a request
 * with no reason attached, and a member who has just registered for an event they are about to
 * stand in a room for is exactly the person for whom the second one is concrete.
 */
export const INSTALL_OFFER_TITLE = 'Put H2HSpark on your home screen';
export const INSTALL_OFFER_BODY =
  'You are going to want this on the day. It opens full screen from your home screen, and it ' +
  'still opens when the venue wi-fi does not.';
/** The same reason, plus the one thing declining costs on iOS — `PLT-008` AC-6. */
export const INSTALL_OFFER_IOS_BODY =
  'You are going to want this on the day. It opens full screen from your home screen, and it ' +
  'still opens when the venue wi-fi does not. In Safari, tap the Share button and choose “Add ' +
  `to Home Screen”. ${IOS_PUSH_NEEDS_INSTALL}`;
export const INSTALL_OFFER_ACTION = 'Add to home screen';
export const INSTALL_OFFER_DISMISS_LABEL = 'Not now';
